import {useEffect, useMemo, useState} from 'react';

import {
    buildPerformerEntriesFromShiftPlans,
    buildTimeblockShiftPlanMap,
    generateEmptyGrid,
    generateTimeBlocksFromShiftPlans,
    isShiftPlanAfterCurrentTime
} from '../helpers/ShiftPlanHelper';

const doShiftPlansHaveGeneratedShifts = (shiftPlans) => {
    if (shiftPlans.length === 0) {
        return false;
    }

    return shiftPlans.some(shiftPlan => {
        if (!shiftPlan?.enrollments?.length) {
            return false;
        }

        return shiftPlan.enrollments.some(enrollment => enrollment?.availability !== null);
    });
};

const buildDisplayedShiftPlan = (firstShiftPlan, inputShiftPlans) => {
    if (!firstShiftPlan) {
        return null;
    }

    let sequence = 1;
    if (inputShiftPlans?.length > 0) {
        const index = inputShiftPlans.findIndex(plan => plan.id === firstShiftPlan.id);
        if (index >= 0) {
            sequence = index + 1;
        }
    }

    return {
        ...firstShiftPlan,
        sequence
    };
};

export default function useShiftPlanCardData({selectedDate, shiftPlanId, inputShiftPlans = []}) {
    const [currentDate, setCurrentDate] = useState('');
    const [shiftPlans, setShiftPlans] = useState([]);
    const [performerEnrollments, setPerformerEnrollments] = useState([]);
    const [availableServiceTypes, setAvailableServiceTypes] = useState([]);
    const [visibleTimeBlocks, setVisibleTimeBlocks] = useState([]);
    const [isProcessingShiftPlanData, setIsProcessingShiftPlanData] = useState(false);
    const [deleteShiftPlanButtonEnabled, setDeleteShiftPlanButtonEnabled] = useState(false);

    useEffect(() => {
        if (!selectedDate) {
            return;
        }

        setCurrentDate(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        const allShiftPlans = inputShiftPlans || [];
        const filteredShiftPlans = shiftPlanId
            ? allShiftPlans.filter(shiftPlan => shiftPlan.id === shiftPlanId)
            : allShiftPlans;

        if (filteredShiftPlans.length === 0) {
            setShiftPlans([]);
            setPerformerEnrollments([]);
            setVisibleTimeBlocks([]);
            setAvailableServiceTypes([]);
            setIsProcessingShiftPlanData(false);
            return;
        }

        setShiftPlans(filteredShiftPlans);

        const flattenedShiftPlans = buildTimeblockShiftPlanMap(filteredShiftPlans);

        const sortedPerformers = buildPerformerEntriesFromShiftPlans(flattenedShiftPlans, currentDate);
        const allServiceTypes = flattenedShiftPlans.flatMap(plan =>
            (plan.availableServiceTypes || []).map(serviceType => ({
                ...serviceType,
                shiftPlanId: plan.shiftPlanId
            }))
        );

        setAvailableServiceTypes(allServiceTypes);
        setPerformerEnrollments(sortedPerformers);

        const generatedTimeBlocks = generateTimeBlocksFromShiftPlans(flattenedShiftPlans, currentDate);
        setVisibleTimeBlocks(generatedTimeBlocks);

        setIsProcessingShiftPlanData(false);
    }, [inputShiftPlans, currentDate, shiftPlanId]);

    useEffect(() => {
        const hasShiftPlansAvailable = shiftPlans.length > 0;
        const hasNoGeneratedShifts = !doShiftPlansHaveGeneratedShifts(shiftPlans);
        const isAfterCurrentTime = isShiftPlanAfterCurrentTime(shiftPlans[0]?.startTime);

        setDeleteShiftPlanButtonEnabled(hasShiftPlansAvailable && hasNoGeneratedShifts && isAfterCurrentTime);
    }, [shiftPlans, currentDate]);

    const emptyGrid = useMemo(() => generateEmptyGrid(shiftPlans), [shiftPlans]);
    const firstShiftPlan = shiftPlans[0];
    const displayedShiftPlan = useMemo(
        () => buildDisplayedShiftPlan(firstShiftPlan, inputShiftPlans),
        [firstShiftPlan, inputShiftPlans]
    );

    return {
        availableServiceTypes,
        currentDate,
        deleteShiftPlanButtonEnabled,
        displayedShiftPlan,
        emptyGrid,
        firstShiftPlan,
        isProcessingShiftPlanData,
        performerEnrollments,
        setIsProcessingShiftPlanData,
        shiftPlans,
        visibleTimeBlocks
    };
}
